import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Dices, GraduationCap, Loader2, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import type { Game, GameSession, Lobby } from '@/lib/types'
import { LOBBY_MAX, LOBBY_STALE_MS, lobbyKind } from '@/lib/lobby'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ErrorNote, Loading, Page } from '@/components/page'
import { SheepTutorial } from '@/components/sheep-tutorial'

/**
 * The Games section (migrations/0046_games.sql).
 *
 * A list, not a menu of hard-coded cards: `games` is a table, so a second game
 * is a row and this page needs no edit to show it. What a game cannot be is
 * generic all the way down — each one has its own board — so the card routes to
 * a path built from the slug and an unknown slug simply has no page.
 *
 * The section is off by default per team. Nothing here enforces that; the nav
 * entry and the route in App.tsx do, and the tables keep their ordinary
 * team-scoped policies either way. Same arrangement as every other feature
 * switch (0044): this hides a tab, it does not withhold data.
 */
export function GamesPage() {
  const { currentTeam } = useAuth()
  const [games, setGames] = React.useState<Game[] | null>(null)
  const [live, setLive] = React.useState<GameSession[]>([])
  const [lobbies, setLobbies] = React.useState<Lobby[]>([])
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)
  const [starting, setStarting] = React.useState<string | null>(null)
  // Which game's rules are open. A game without a tutorial simply has no
  // button, rather than a button that opens an apology.
  const [tutorial, setTutorial] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (!currentTeam) return
    const [g, s, l] = await Promise.all([
      sb().from('games').select('*').eq('active', true).order('display_order'),
      sb()
        .from('game_sessions')
        .select('*')
        .eq('team_id', currentTeam.id)
        .neq('status', 'finished'),
      sb()
        .from('lobbies')
        .select('*')
        .eq('team_id', currentTeam.id)
        .in('status', ['open', 'started']),
    ])
    if (g.error) return setError(g.error.message)
    setGames((g.data ?? []) as Game[])
    setLive((s.data ?? []) as GameSession[])
    // Past the sweep's thirty minutes a lobby is a forgotten tab, not a game:
    // hide it so the card offers Start, whose open_lobby call retires it.
    const now = Date.now()
    setLobbies(
      ((l.data ?? []) as Lobby[]).filter(
        (x) => now - new Date(x.started_at ?? x.created_at).getTime() <= LOBBY_STALE_MS,
      ),
    )
  }, [currentTeam])

  React.useEffect(() => {
    void load()
  }, [load])

  async function start(game: Game) {
    if (!currentTeam) return
    setStarting(game.id)
    // The RPC allocates the sides and hands back the race in progress when
    // somebody else pressed this first — so two people opening the page
    // together end up in one race rather than two.
    const { data, error: err } = await sb().rpc('start_game_session', {
      p_team_id: currentTeam.id,
      p_slug: game.slug,
    })
    setStarting(null)
    if (err) return setError(err.message)
    const session = data as GameSession
    window.location.assign(`/games/${game.slug}/${session.id}`)
  }

  // A lobby game (0061): opening one asks the team to join, and an open one
  // already is the answer — the RPC hands it back rather than a second lobby.
  async function openLobby(game: Game) {
    if (!currentTeam) return
    const kind = lobbyKind(game.slug)
    if (!kind) return
    setStarting(game.id)
    const { data, error: err } = await sb().rpc('open_lobby', {
      p_team_id: currentTeam.id,
      p_kind: kind.kind,
      p_max_players: LOBBY_MAX,
    })
    setStarting(null)
    if (err) return setError(err.message)
    navigate(kind.path((data as Lobby).id))
  }

  if (!games && !error) return <Loading label="Opening the games room…" />

  return (
    <Page title="Games">
      {error && <ErrorNote error={error} />}

      <div className="grid gap-4 sm:grid-cols-2">
        {(games ?? []).map((game) => {
          // A one-player game has no session to start or join (0060): the card
          // links straight to its page, and never calls start_game_session,
          // which would write a team session and deal the room into sides.
          // Checked before `solo`: a lobby game may be started by its host alone,
          // so its min_players is 1 too.
          const lobbyGame = lobbyKind(game.slug)
          const lobby = lobbyGame ? lobbies.find((l) => l.kind === lobbyGame.kind) : undefined
          const solo = !lobbyGame && game.min_players <= 1
          const open = solo || lobbyGame ? undefined : live.find((s) => s.game_id === game.id)
          return (
            <Card key={game.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base">{game.title}</CardTitle>
                    <CardDescription className="mt-1">{game.description}</CardDescription>
                  </div>
                  <Dices className="h-5 w-5 shrink-0 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  {lobbyGame
                    ? `Up to ${LOBBY_MAX} players · each on their own screen`
                    : solo
                      ? 'One player · on your own'
                      : `${game.min_players}+ players · two sides`}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {lobbyGame ? (
                    lobby ? (
                      <>
                        <Badge variant={lobby.status === 'open' ? 'warning' : 'success'}>
                          {lobby.status === 'open' ? 'Waiting for players' : 'In progress'}
                        </Badge>
                        <Button asChild size="sm">
                          <Link to={lobbyGame.path(lobby.id)}>
                            {lobby.status === 'open' ? 'Go to the lobby' : 'Watch'}
                          </Link>
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => void openLobby(game)} disabled={starting === game.id}>
                        {starting === game.id ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Setting up…
                          </>
                        ) : (
                          'Start a game'
                        )}
                      </Button>
                    )
                  ) : solo ? (
                    <Button asChild size="sm">
                      <Link to={`/games/${game.slug}`}>Play</Link>
                    </Button>
                  ) : open ? (
                    <>
                      <Badge variant="success">In progress</Badge>
                      <Button asChild size="sm">
                        <Link to={`/games/${game.slug}/${open.id}`}>Join the race</Link>
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => void start(game)} disabled={starting === game.id}>
                      {starting === game.id ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Setting up…
                        </>
                      ) : (
                        'Start a game'
                      )}
                    </Button>
                  )}
                  {game.slug === 'sheep-race' && (
                    <Button variant="outline" size="sm" onClick={() => setTutorial(game.slug)}>
                      <GraduationCap /> Tutorial
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {tutorial === 'sheep-race' && <SheepTutorial onClose={() => setTutorial(null)} />}

      {games?.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No games in the catalogue yet.
        </p>
      )}
    </Page>
  )
}
